import { Link } from 'react-router';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useSEO, COMPETITIVE_KEYWORD_CLUSTERS, mergeKeywordSets } from '../lib/seo';
import { StockHeroStrip, WarmMarketingBackdrop } from '../components/marketing/MarketingVisuals';
import { TensionRiMicroCoachShowcase } from '../components/marketing/TensionRiMicroCoachShowcase';
import { ArrowRight, MessageCircle, Shield, Sparkles, Zap } from 'lucide-react';

export default function FeatureMicroCoach() {
  useSEO({
    title: 'Conversation Micro-Coach | TENSION',
    description:
      'Draft safer messages before you send. Conversation Micro-Coach acts like an AI couples therapy assistant for calmer rewrites and clearer intent.',
    keywords: mergeKeywordSets(
      COMPETITIVE_KEYWORD_CLUSTERS.communication as unknown as string[],
      ['conversation coach', 'text message rewrite', 'conflict de-escalation', 'relationship communication AI'],
    ),
    canonical: '/features/conversation-micro-coach',
    ogType: 'website',
    twitterCard: 'summary_large_image',
  });

  return (
    <WarmMarketingBackdrop>
      <div className="mx-auto max-w-5xl space-y-10 px-4 py-10 md:py-14">
        <header className="grid gap-8 overflow-hidden rounded-3xl border border-fuchsia-200/60 bg-white/80 shadow-xl shadow-fuchsia-900/10 backdrop-blur-md lg:grid-cols-[1.05fr_0.95fr] lg:items-center">
          <div className="space-y-4 p-6 md:p-10">
            <p className="inline-flex items-center gap-2 rounded-full bg-fuchsia-100 px-3 py-1 text-xs font-bold uppercase tracking-wider text-fuchsia-800 ring-1 ring-fuchsia-200/80">
              <Sparkles className="h-3.5 w-3.5" aria-hidden />
              Feature spotlight
            </p>
            <h1 className="text-3xl font-black tracking-tight text-neutral-900 md:text-4xl lg:text-[2.5rem] lg:leading-tight">
              AI couples therapy support for daily communication
            </h1>
            <p className="max-w-xl text-base leading-relaxed text-neutral-600 md:text-lg">
              Paste a draft before sending. Micro-Coach flags escalation risk and proposes a clearer, calmer version—so
              intent survives the moment.
            </p>
            <div className="flex flex-wrap gap-3 pt-2">
              <Link to="/home#relationship-intelligence">
                <Button className="bg-gradient-to-r from-fuchsia-600 to-violet-600 px-6 text-white shadow-lg hover:opacity-95">
                  Try it in Relationship Intelligence <ArrowRight className="ml-1 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/guides">
                <Button variant="outline" className="border-fuchsia-200 bg-white/80">
                  Read guides
                </Button>
              </Link>
            </div>
          </div>
          <div className="relative flex min-h-[220px] flex-col justify-center p-4 lg:min-h-full lg:p-6">
            <div className="relative z-10">
              <TensionRiMicroCoachShowcase
                variant="light"
                className="lg:[&_img]:max-h-[min(380px,48vh)]"
              />
            </div>
            <div
              className="pointer-events-none absolute inset-0 bg-gradient-to-r from-white/40 via-transparent to-transparent lg:from-white/30"
              aria-hidden
            />
          </div>
        </header>

        <StockHeroStrip
          stockKey="handsWalking"
          alt="Two people walking together, holding hands"
          className="shadow-lg"
        />

        <div className="grid gap-4 md:grid-cols-3">
          {[
            {
              title: 'Risk preview',
              body: 'See likely emotional impact before the message is sent.',
              icon: MessageCircle,
            },
            {
              title: 'Calmer rewrites',
              body: 'Keep your meaning, lower the heat.',
              icon: Zap,
            },
            {
              title: 'Faster repair',
              body: 'Turn conflict-prone wording into collaboration language.',
              icon: Shield,
            },
          ].map(({ title, body, icon: Icon }) => (
            <Card
              key={title}
              className="border-fuchsia-100/80 bg-white/90 p-6 shadow-md transition-all hover:-translate-y-0.5 hover:shadow-lg"
            >
              <Icon className="h-8 w-8 text-fuchsia-600" aria-hidden />
              <h2 className="mt-3 font-semibold text-neutral-900">{title}</h2>
              <p className="mt-2 text-sm leading-relaxed text-neutral-600">{body}</p>
            </Card>
          ))}
        </div>

        <Card className="border-violet-200/80 bg-gradient-to-br from-violet-50/90 to-fuchsia-50/80 p-8 shadow-md">
          <h2 className="text-xl font-semibold text-neutral-900">Explore more</h2>
          <p className="mt-2 text-sm text-neutral-600">
            Pair Micro-Coach with Relationship Intelligence for full pattern context—not just one-off rewrites.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/features/relationship-intelligence">
              <Button variant="outline" className="border-violet-300 bg-white/90">
                Intelligence suite page
              </Button>
            </Link>
            <Link to="/best-couples-app">
              <Button variant="outline" className="border-violet-300 bg-white/90">
                Top couples app options
              </Button>
            </Link>
            <Link to="/compare/tension-vs-paired">
              <Button variant="outline" className="border-violet-300 bg-white/90">
                TENSION vs Paired
              </Button>
            </Link>
          </div>
        </Card>
      </div>
    </WarmMarketingBackdrop>
  );
}
